import React, { useMemo } from "react";
import { Box, useTheme, Typography } from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import WebAssetRoundedIcon from "@mui/icons-material/WebAssetRounded";
import ceLogoFull from "assets/ce-logo-full-h.png";

/**
 * DetailedLogsDocument — the OPI "Detailed Logs" PDF document.
 *
 * Renders one page per sheet of US-Letter LANDSCAPE paper (27.9cm × 21.6cm)
 * using the exact page shell, header treatment, footer/contact block and
 * colored accent bars of the invoice pages in
 * `scenes/management/invoices/InvoiceItem.jsx`, with the OPI detail rows
 * laid out in a compact table. Landscape is the natural fit: the detail
 * table has far more columns than rows, and wide pages keep more rows per
 * sheet.
 *
 * It is consumed only by the off-screen capture flow in
 * `InterpretationOPIUpload.exportDetailedPDFs`, which captures the rendered
 * pages with `generateInvoicePdfBlob` (html2canvas + jsPDF, called with
 * `orientation: "landscape"`) — the same pipeline that produces invoice
 * PDFs — so the exported documents are visually identical to invoices.
 *
 * Capture contract: the component renders a wrapper Box whose DIRECT
 * children are the page elements (the same contract `<Invoice />` honors in
 * InvoiceItem.jsx / InvoiceProfile.jsx — `generateInvoicePdfBlob` captures
 * `host.children` as one PDF page per child).
 */

// US Letter LANDSCAPE page constants — the same sheet as InvoiceItem.jsx,
// rotated (21.6cm × 27.9cm portrait → 27.9cm × 21.6cm landscape).
const PAGE_WIDTH = "27.9cm"; // US Letter landscape width
const PAGE_HEIGHT = "21.6cm"; // US Letter landscape height
const PAGE_PADDING = "1.5cm";

const WORK_SANS = ["Work Sans", "sans-serif"].join(",");

// The 10 detail columns, in the same order the export has always used. The
// widths total 23.1cm; adding the 9 × 0.2cm column gaps brings the grid to
// exactly the 24.9cm landscape content width (27.9cm − 2 × 1.5cm paddings),
// so the last column ("Other Participants") is never clipped.
export const DETAILED_LOGS_COLUMNS = [
  { key: "Requestor", width: "2.2cm", align: "left" },
  { key: "Account Name", width: "2.8cm", align: "left" },
  { key: "Date/Time", width: "2.6cm", align: "center" },
  { key: "Language", width: "2.0cm", align: "left" },
  { key: "Service Type", width: "1.6cm", align: "center" },
  { key: "Client Billable Duration", width: "2.0cm", align: "center" },
  { key: "Account Charge", width: "1.8cm", align: "center" },
  { key: "From Number", width: "1.9cm", align: "center" },
  { key: "Requestor Device Info", width: "3.0cm", align: "left" },
  { key: "Other Participants", width: "3.2cm", align: "left" },
];

export const DETAILED_LOGS_HEADERS = DETAILED_LOGS_COLUMNS.map((c) => c.key);

const TABLE_GRID_TEMPLATE = DETAILED_LOGS_COLUMNS.map((c) => c.width).join(
  " ",
);

// ---------------------------------------------------------------------------
// Row-height estimation → pagination
//
// The pages are fixed-height paper sheets (overflow hidden), so rows must be
// split BEFORE rendering: pick the page break whose estimated heights fit the
// usable area. The estimates are deliberately conservative (slightly
// over-estimate lines) — an extra page is harmless, a clipped billing row is
// not.
// ---------------------------------------------------------------------------
const CM_TO_PX = 37.7953; // 96dpi
const DATA_FONT_PX = 9; // table cell type scale (landscape width supports the bump)
const LINE_HEIGHT_PX = DATA_FONT_PX * 1.15; // matches lineHeight 1.15 in styles
const ROW_VERTICAL_PADDING_PX = 5; // py 0.05cm both sides + 1px hairline
const GLYPH_PX = 4.8; // conservative average glyph width at 9px Work Sans
// Usable page height: landscape sheet 21.6cm ≈ 816px; minus 1.5cm top
// padding (~57px), three 0.5cm flex gaps (~57px), the two 1cm footer bands
// (~76px), the header block incl. the new Period row (~116px) and the table
// head (~60px) leaves ~450px of row space. 420 leaves a safety margin
// against estimation error.
const PAGE_ROW_BUDGET_PX = 420;

const COLUMN_CHARS_PER_LINE = DETAILED_LOGS_COLUMNS.map((col) =>
  Math.max(4, Math.floor((parseFloat(col.width) * CM_TO_PX) / GLYPH_PX)),
);

const estimateCellLines = (value, charsPerLine) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (!text) return 1;
  return text
    .split("\n")
    .reduce(
      (sum, segment) =>
        sum + Math.max(1, Math.ceil(segment.length / charsPerLine)),
      0,
    );
};

const estimateRowHeightPx = (row) => {
  let lines = 1;
  DETAILED_LOGS_COLUMNS.forEach((col, index) => {
    lines = Math.max(
      lines,
      estimateCellLines(row?.[col.key], COLUMN_CHARS_PER_LINE[index]),
    );
  });
  return lines * LINE_HEIGHT_PX + ROW_VERTICAL_PADDING_PX;
};

export const paginateDetailedLogsRows = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  const pages = [];
  let page = [];
  let usedPx = 0;

  source.forEach((row) => {
    const rowPx = estimateRowHeightPx(row);
    if (page.length > 0 && usedPx + rowPx > PAGE_ROW_BUDGET_PX) {
      pages.push(page);
      page = [];
      usedPx = 0;
    }
    page.push(row);
    usedPx += rowPx;
  });

  if (page.length > 0) pages.push(page);
  return pages.length > 0 ? pages : [[]];
};

// ---------------------------------------------------------------------------
// Page footer — byte-for-byte the invoice footer (dark contact band +
// #dcc7a9/#e8e6e1 accent bars). The {page}/{total} chip appears only when
// the document has more than one page, mirroring the invoice's multi-page
// footer (the single-page invoice has no page number).
// ---------------------------------------------------------------------------
const DetailedLogsFooter = ({ clientName, pageNumber, totalPages }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <Box
        sx={{
          display: "flex",
          height: "1cm",
          width: "calc(100% + 3cm)",
          ml: "-1.5cm",
          mr: "-1.5cm",
          position: "relative",
          justifyContent: "flex-end",
        }}
      >
        {totalPages > 1 && (
          <Box
            sx={{
              width: "10%",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {pageNumber}/{totalPages}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            width: "90%",
            bgcolor: "#333333",
            borderTopLeftRadius: "9999px",
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            px: "0.5cm",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.2cm",
            }}
          >
            <EmailRoundedIcon
              sx={{
                fontSize: "0.5cm",
                color: theme.palette.background.default,
              }}
            />
            <Typography
              variant="body1"
              component="a"
              href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Detailed Logs for ${clientName}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.background.default,
                fontSize: 13,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              amer@communicationessentialsllc.com
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.2cm",
            }}
          >
            <WebAssetRoundedIcon
              sx={{
                fontSize: "0.5cm",
                color: theme.palette.background.default,
              }}
            />
            <Typography
              variant="body1"
              component="a"
              href="https://communicationessentialsllc.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.background.default,
                fontSize: 13,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              communicationessentialsllc.com
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.2cm",
            }}
          >
            <PhoneRoundedIcon
              sx={{
                fontSize: "0.5cm",
                color: theme.palette.background.default,
              }}
            />
            <Typography
              variant="body1"
              component="a"
              href="tel:+17179922885"
              sx={{
                color: theme.palette.background.default,
                fontSize: 13,
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              (717) 992-2885
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          height: "1cm",
          width: "calc(100% + 3cm)",
          ml: "-1.5cm",
          mr: "-1.5cm",
          position: "relative",
        }}
      >
        {/* Background boxes - 50% each */}
        <Box
          sx={{
            position: "absolute",
            width: "50%",
            height: "100%",
            bgcolor: "#dcc7a9",
            left: 0,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "50%",
            height: "100%",
            bgcolor: "#e8e6e1",
            right: 0,
            zIndex: 0,
          }}
        />

        {/* Colored boxes */}
        <Box
          sx={{
            width: "39.08%",
            bgcolor: theme.palette.primary.main,
            borderTopRightRadius: "9999px",
            position: "relative",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            width: "34.80%",
            bgcolor: "#dcc7a9",
            borderTopRightRadius: "9999px",
            position: "relative",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            width: "26.12%",
            bgcolor: "#e8e6e1",
            position: "relative",
            zIndex: 1,
          }}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Single document page — the invoice page shell (InvoiceItem + the
// InvoicePage* header) with the logs table in place of the items list.
// ---------------------------------------------------------------------------
const DetailedLogsPage = ({
  clientName,
  periodLabel,
  rows,
  pageNumber,
  totalPages,
}) => {
  const theme = useTheme();

  return (
    <Box
      className="InvoiceItem"
      sx={{
        width: PAGE_WIDTH,
        minWidth: PAGE_WIDTH,
        maxWidth: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        minHeight: PAGE_HEIGHT,
        maxHeight: PAGE_HEIGHT,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.primary.text,
        // Export-only document: pages are always captured with square
        // corners (the invoice export passes noBorderRadius and the capture
        // pipeline also zeroes .InvoiceItem radii).
        borderRadius: 0,
        border: `0px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
        pt: PAGE_PADDING,
        pl: PAGE_PADDING,
        pr: PAGE_PADDING,
        pb: 0,
        gap: "0.5cm",
      }}
    >
      {/* Header — same treatment as every invoice page: primary-color title
          in the display font with the dynamic value in Work Sans, CE full
          logo right-aligned. The grid is "1fr auto" so the title block sits
          left and the logo stays flush with the RIGHT content edge on the
          wider landscape page (the portrait invoice's fixed 10cm/8cm split
          would leave it ~6cm short of that edge here). */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          columnGap: "0.6cm",
          alignItems: "start",
          maxWidth: "100%",
          mb: "0cm",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
            >
              Detailed Logs
            </Typography>
          </Box>
          <Box sx={{ my: "0.75rem" }} />
          {/* Period — the file's coverage range (each upload is one whole
              month). Same label/value pattern as the Client row; omitted
              entirely when no label could be derived. */}
          {periodLabel ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: "0.25cm",
                mb: "0cm",
                minWidth: 0,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={500}
                color={theme.palette.primary.main}
                sx={{ flexShrink: 0 }}
              >
                Period
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: WORK_SANS,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {periodLabel}
              </Typography>
            </Box>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "0.25cm",
              mb: "0cm",
              minWidth: 0,
            }}
          >
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
              sx={{ flexShrink: 0 }}
            >
              Client
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: WORK_SANS,
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              {clientName}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            width: "100%",
            ml: "auto",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={ceLogoFull}
            alt="Communication Essentials Logo"
            sx={{
              justifySelf: "end",
              maxWidth: "7cm",
              maxHeight: "2.5cm",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Detail rows table — borrows the invoice items table treatment:
          bg_light head band, primary-colored labels, hairline dividers. */}
      <Box sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: TABLE_GRID_TEMPLATE,
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: "0.2cm",
            pt: "0.4cm",
            fontWeight: 700,
            columnGap: "0.2cm",
            maxWidth: "100%",
          }}
        >
          {DETAILED_LOGS_COLUMNS.map((col) => (
            <Box
              key={col.key}
              sx={{
                minWidth: 0,
                textAlign: col.align === "center" ? "center" : "left",
              }}
            >
              <Typography
                variant="h6"
                color={theme.palette.primary.main}
                sx={{
                  fontSize: 10,
                  lineHeight: 1.2,
                  overflowWrap: "anywhere",
                }}
              >
                {col.key}
              </Typography>
            </Box>
          ))}
        </Box>

        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: TABLE_GRID_TEMPLATE,
              py: "0.05cm",
              borderBottom: `1px solid ${theme.palette.divider}`,
              columnGap: "0.2cm",
              maxWidth: "100%",
            }}
          >
            {DETAILED_LOGS_COLUMNS.map((col) => (
              <Box
                key={col.key}
                sx={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    col.align === "center" ? "center" : "flex-start",
                }}
              >
                <Typography
                  variant="body3"
                  sx={{
                    fontSize: DATA_FONT_PX,
                    lineHeight: 1.15,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    textAlign: col.align,
                    width: "100%",
                  }}
                >
                  {row?.[col.key] === null || row?.[col.key] === undefined
                    ? ""
                    : String(row[col.key])}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      <DetailedLogsFooter
        clientName={clientName}
        pageNumber={pageNumber}
        totalPages={totalPages}
      />
    </Box>
  );
};

/**
 * The full document: one `DetailedLogsPage` per estimated chunk of rows.
 * The forwarded ref sits on the wrapper Box — its direct children are the
 * pages, which is what `generateInvoicePdfBlob` iterates over.
 */
const DetailedLogsDocument = React.forwardRef(
  ({ clientName = "", periodLabel = "", rows = [] }, ref) => {
    const pages = useMemo(() => paginateDetailedLogsRows(rows), [rows]);

    return (
      <Box
        ref={ref}
        data-client={clientName}
        sx={{ display: "flex", flexDirection: "column", gap: "1cm" }}
      >
        {pages.map((pageRows, index) => (
          <DetailedLogsPage
            key={index}
            clientName={clientName}
            periodLabel={periodLabel}
            rows={pageRows}
            pageNumber={index + 1}
            totalPages={pages.length}
          />
        ))}
      </Box>
    );
  },
);

export default DetailedLogsDocument;
